<script lang="ts">
/**
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.1.0
 * @copyright  Copyright (C) 2024, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 * @changelog
 *  2024-08-23 (1.0.0) (jhoward)
 *    - Refactor textual default item from <textual-menu> to <default-item>.
 *
 *  2024-08-23 (1.1.0) (jhoward)
 *    - Handle selected menu items via store in textual <default-item>.
 *    - Support pricing types in textual <default-item>.
 *    - Report item content rows in textual <default-item>.
 *    - Refactor textual menu item CSS to textual <default-item>.
 */

import {defineComponent, PropType} from 'vue'
import MenuTag from "../../menu-tag.vue";
import {Menu, MenuItem} from "../../defs";
import {
  computedIsScheduled, methodGetPricingTypes,
  methodGetItemBIN,
  methodRemoveBINFromTitle,
  methodPricePerType
} from "../../../../helpers/menu-sections";

export default defineComponent({
  name: "defaultItem",
  emits: {
    'rows': (rows: number) => true
  },
  components: {MenuTag},
  props: {
    menu: {
      type: Object as PropType<Menu>,
      required: true
    },
    item: {
      type: Object as PropType<MenuItem>,
      required: true
    },
    itemIndex: {
      type: Number,
      required: true
    },
  },
  mounted() {
    this.$emit('rows', this.itemRows);
  },
  data() {
    return {
      pricingTypes: this.getPricingTypes([this.item]).map(priceType => {
        return [
          priceType,
          this.getPricePerType(
            this.item.mi_price,
            priceType,
            this.item.mi_price
          )
        ];
      }) as [string, string][]
    }
  },
  watch: {
    itemRows() {
      this.$emit('rows', this.itemRows);
    },
  },
  computed: {
    isScheduled: computedIsScheduled,
    containerClasses(): { [key: string]: boolean } {
      const output: { [key: string]: boolean } = {
        'item-highlight': !!this.item.mi_highlight
      };
      output['item-' + this.itemIndex] = true;
      output['delay-' + (this.itemIndex % 2)] = true;

      return output;
    },
    isPricingTypesShown(): boolean {
      return !!this.pricingTypes.length;
    },
    isBodyShown(): boolean {
      return !!this.item.mi_subtitle.length || !!this.item.mi_description.length;
    },
    isTagsShown(): boolean {
      return !!this.item.tags.length;
    },
    itemRows(): number {
      let output = 1; //title
      if (this.isBodyShown) {
        output ++;
      }
      if (this.isTagsShown) {
        output ++;
      }
      if (this.isPricingTypesShown) {
        output ++;
      }

      return output;
    },
    bin(): string {
      return this.getItemBin(this.item);
    },
    isBinShown(): boolean {
      return !!this.bin.length;
    },
  },
  methods: {
    getItemBin: methodGetItemBIN,
    getTitleWithoutBin: methodRemoveBINFromTitle,
    getPricingTypes: methodGetPricingTypes,
    getPricePerType: methodPricePerType
  }
})
</script>

<template>
  <div v-show="isScheduled" :id="'textual_menu--item-' + item.mi_id" class="item textual_menu--item no-image"
       :class="containerClasses">
    <h3>
      <a class="item_button--container item_button--container-select material-icons"
         @click.prevent="$store.commit('toggleMenuItemSelected', [item.mi_id, menu.menu_id])" href="#"
         :class="{'item_button--container-active': -1 !== $store.state.selectedMenuItemIds.indexOf(item.mi_id)}"
         :title="(-1 !== $store.state.selectedMenuItemIds.indexOf(item.mi_id)) ? 'Click to remove this item from saved list' : 'Click to save this item for later'"></a>
      <span v-if="isBinShown">
        <span class=menu_item--bin>{{ bin }}</span>
        <span v-html="getTitleWithoutBin(item)"></span>
      </span>
      <span v-else v-html="item.mi_title"></span>
      <div v-if="item.mi_market_price > 0" class="menu_item--price">MP</div>
      <div v-else-if="item.mi_price.length && !isPricingTypesShown" class="menu_item--price" v-html="item.mi_price"></div>
    </h3>
    <ul v-if="isPricingTypesShown" class="menu_item--prices">
      <li v-for="pricingType in pricingTypes" :key="pricingType[0]" class="menu_item--price">{{ pricingType[0] }} {{ pricingType[1] }}</li>
    </ul>
    <div v-if="isTagsShown" class="menu_item--tags">
      <menu-tag v-for="tag in item.tags" :key="tag.mt_id" :tag="tag" menu-type="textual"></menu-tag>
    </div>
    <div v-if="isBodyShown" class="menu_item--body item-body">
      <h4 v-if="item.mi_subtitle.length" v-html="item.mi_subtitle"></h4>
      <p v-if="item.mi_description.length" v-html="item.mi_description"></p>
    </div>
  </div>
</template>

<style scoped>
.textual_menu--item {
  display: block;
  overflow: visible;
  width: initial !important;
  font-size: 0.9rem;
}

.textual_menu--item h3 {
  font-size: 1rem;
  overflow: initial;
  display: block;
  border-bottom: none;
}

.textual_menu--item h4 {
  margin-top: -6px;
  margin-bottom: 8px;
}

.menu_item--price {
  display: block;
  font-size: 0.9em;
  color: #545454;
  padding-top: 0.4em;
}

.menu_item--prices {
  list-style: none;
  padding: 0 12px 12px 12px;
  margin: 0;
}

.menu_item--tags {
  font-size: 0.8em;
}

.menu_item--bin {
  font-size: 0.9em;
  color: #666;
  margin-right: 0.5em;
}

.menu_item--body {
  border-top: 1px solid #D1D2D4;
}
</style>